/**
 * Object-type-agnostic list/filter over the manifest, backing ObjectPicker.
 * Each pickable kind's identity field differs (Entity.id, Surface.id,
 * CloudFunction.name, EventType.type, FirestorePath.path) — this module is
 * the one place that normalizes them into a common shape.
 */
import type { AtlasManifest } from '@atlas-manifest/types';

export type PickableKind = 'entity' | 'surface' | 'cloud-function' | 'event-type' | 'firestore-path';

export interface PickableObject {
  kind: PickableKind;
  id: string;
  subtitle: string;
}

export function listPickable(manifest: AtlasManifest, kind: PickableKind): PickableObject[] {
  switch (kind) {
    case 'entity':
      return manifest.entities.map((e) => ({ kind, id: e.id, subtitle: e.domain }));
    case 'surface':
      return manifest.surfaces.map((s) => ({ kind, id: s.id, subtitle: s.domain }));
    case 'cloud-function':
      return manifest.cloudFunctions.map((f) => ({ kind, id: f.name, subtitle: f.domain }));
    case 'event-type':
      return manifest.events.map((e) => ({ kind, id: e.type, subtitle: e.category }));
    case 'firestore-path':
      return manifest.paths.map((p) => ({ kind, id: p.path, subtitle: p.entityId }));
  }
}

export function filterPickable(items: readonly PickableObject[], query: string, cap = 20): PickableObject[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...items].sort((a, b) => a.id.localeCompare(b.id)).slice(0, cap);
  const scored: { item: PickableObject; score: number }[] = [];
  for (const item of items) {
    const id = item.id.toLowerCase();
    const sub = item.subtitle.toLowerCase();
    let score = 0;
    if (id === q) score = 1000;
    else if (id.startsWith(q)) score = 500;
    else if (id.includes(q)) score = 100;
    else if (sub.startsWith(q)) score = 60;
    else if (sub.includes(q)) score = 20;
    if (score > 0) scored.push({ item, score });
  }
  return scored
    .sort((a, b) => b.score - a.score || a.item.id.localeCompare(b.item.id))
    .slice(0, cap)
    .map((s) => s.item);
}
